import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { toastError } from 'components/billing/common/utils';
import { api } from 'controllers/network/apiClient';

// Business types matching the Python backend
export const BusinessType = {
  SOLE_PROPRIETOR: 'SOLE_PROPRIETOR',
  PRIVATE_PROFIT: 'PRIVATE_PROFIT',
  PUBLIC_PROFIT: 'PUBLIC_PROFIT',
  NON_PROFIT: 'NON_PROFIT',
  GOVERNMENT: 'GOVERNMENT',
} as const;
export type BusinessType = typeof BusinessType[keyof typeof BusinessType];

export const BusinessRegistrationAuthority = {
  EIN: 'EIN', // US - Employer Identification Number
  CBN: 'CBN', // Canada - Canadian Business Number
  CN: 'CN', // Great Britain - Company Number
  ACN: 'ACN', // Australia - Australian Company Number
  CIN: 'CIN', // India - Corporate Identity Number
  VAT: 'VAT', // Estonia - VAT Number
  VATRN: 'VATRN', // Romania - VAT Registration Number
  RN: 'RN', // Israel - Registration Number
  OTHER: 'OTHER',
} as const;
export type BusinessRegistrationAuthority =
  typeof BusinessRegistrationAuthority[keyof typeof BusinessRegistrationAuthority];

// Request body for PUT /api/v2/business_registration
export interface UpdateBusinessRegistrationInfo {
  businessType: BusinessType;
  registrationAuthority: BusinessRegistrationAuthority;
  registrationNumber: string;
  registrationCountry: string; // ISO 3166-1 alpha-2 country code (e.g., "US", "CA")
}

// Response from PUT /api/v2/business_registration
export interface BusinessRegistrationInfo {
  businessType: BusinessType;
  registrationAuthority: BusinessRegistrationAuthority;
  registrationNumber: string;
  registrationCountry: string; // Will be normalized to uppercase by backend
}

const BUSINESS_REGISTRATION_QUERY_KEY = 'business_registration';
const BUSINESS_REGISTRATION_URL = '/v2/shops/business_registration';

export function useGetBusinessRegistration(): UseQueryResult<
  BusinessRegistrationInfo | undefined
> {
  return useQuery({
    queryKey: [BUSINESS_REGISTRATION_QUERY_KEY],
    queryFn: () => api.get<BusinessRegistrationInfo>(BUSINESS_REGISTRATION_URL),
    retry: false,
    // Don't show error toast for 404 - it's expected when no business registration exists
  });
}

export function useUpdateBusinessRegistration(): UseMutationResult<
  BusinessRegistrationInfo,
  unknown,
  UpdateBusinessRegistrationInfo
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateBusinessRegistrationInfo) => {
      return api.put<BusinessRegistrationInfo>(BUSINESS_REGISTRATION_URL, data);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([BUSINESS_REGISTRATION_QUERY_KEY], data);
    },
    onError: toastError,
  });
}
